import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FieldValues, UseFormReturn, useForm } from "react-hook-form";
import { Schema } from "zod";

interface StatefulFormParams {
    schema?: Schema;
    defaultValues?: Record<string, string | number | boolean>;
    initialValues?: FieldValues;
    suppressReset?: boolean;
}

export interface IStatefulFormHook {
    methods: UseFormReturn<any, unknown, any>;
    isDirty: boolean;
    isSubmitting: boolean;
}

/**
 * This hook is used to create a StatefulForm component that uses react-hook-form and zod to validate form data.
 * @description If no formState needs to be accessed, then the SmartForm component can be used without this hook.
 * @param {StatefulFormParams} params Optional: schema, defaultValues, initialValues.
 * @param schema The zod schema used to validate the form data.
 * @param defaultValues The default values for the form, used to determine if the form fields have been touched
 * and to reset the form after a successful submission.
 * @param initialValues The initial values for the form fields.
 * @param suppressReset If true, the form will not be reset after a successful submission.
 * @returns {IStatefulFormHook} A StatefulForm component and the react-hook-form methods.
 */
export function useStatefulForm({
    defaultValues,
    schema,
    initialValues,
    suppressReset,
}: StatefulFormParams): IStatefulFormHook {
    const methods = useForm({ defaultValues, resolver: schema ? zodResolver(schema) : undefined });

    const {
        formState: { isSubmitSuccessful, isDirty, isSubmitting },
        reset,
        setValue,
    } = methods;

    useEffect(() => {
        if (isSubmitSuccessful && !suppressReset) {
            reset(defaultValues);
        }
    }, [defaultValues, reset, isSubmitSuccessful, suppressReset]);

    useEffect(() => {
        const values = initialValues ?? defaultValues;
        if (values) {
            Object.keys(values).forEach((key) => setValue(key, values[key]));
        }
    }, [initialValues, defaultValues, setValue]);

    return useMemo<IStatefulFormHook>(
        () => ({
            methods,
            isDirty,
            isSubmitting,
        }),
        [methods, isDirty, isSubmitting]
    );
}
